// TMOS User Approval Modal Component
// Date: December 19, 2025
// Purpose: Modal for approve/reject/merge actions with role and hierarchy assignment

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { UserApprovalRequest, userApprovalService } from '@/services/userApprovalService';
import { businessPositionService } from '@/services/businessPositionService';
import { toast } from 'react-hot-toast';
import api from '@/services/api';

interface UserApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  approval: UserApprovalRequest;
  action: 'approve' | 'reject' | 'merge';
  onSuccess: () => void;
}

const SYSTEM_ROLES = [
  { value: 'T_ADMIN', label: 'Tenant Administrator' },
  { value: 'T_USER', label: 'Tenant User' },
  { value: 'C_ADMIN', label: 'Company Administrator' },
  { value: 'C_USER', label: 'Company User' },
  { value: 'P_ADMIN', label: 'Project Administrator' },
  { value: 'P_USER', label: 'Project User' }
];

export const UserApprovalModal: React.FC<UserApprovalModalProps> = ({
  isOpen,
  onClose,
  approval,
  action,
  onSuccess
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [assignedRole, setAssignedRole] = useState<string>('');
  const [assignedPosition, setAssignedPosition] = useState<string>('');
  const [selectedHierarchies, setSelectedHierarchies] = useState<Record<string, string>>({});
  const [mergeToTenantId, setMergeToTenantId] = useState<string>('');
  const [availableTenants, setAvailableTenants] = useState<Array<{ id: string; name: string }>>([]);
  
  const [positions, setPositions] = useState<any[]>([]);
  const [hierarchies, setHierarchies] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && action === 'approve') {
      loadData();
    } else if (isOpen && action === 'merge') {
      loadTenants();
    }
  }, [isOpen, action]);

  const loadData = async () => {
    try {
      const [positionsRes, geoRes, funcRes, busRes] = await Promise.all([
        businessPositionService.getActivePositions(),
        api.get('/generic/geographic-hierarchies').catch(() => ({ data: { data: [] } })),
        api.get('/generic/functional-hierarchies').catch(() => ({ data: { data: [] } })),
        api.get('/generic/business-hierarchies').catch(() => ({ data: { data: [] } }))
      ]);

      setPositions(positionsRes);
      setHierarchies([
        ...(geoRes.data.data || []).map((h: any) => ({ ...h, type: 'geographic' })),
        ...(funcRes.data.data || []).map((h: any) => ({ ...h, type: 'functional' })),
        ...(busRes.data.data || []).map((h: any) => ({ ...h, type: 'business' }))
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadTenants = async () => {
    try {
      // Load available tenants for merging
      const response = await api.get('/tenants');
      const tenants = response.data.data || response.data || [];
      setAvailableTenants(tenants.map((t: any) => ({ id: t.id, name: t.name || t.company_name })));
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (action === 'approve') {
        await userApprovalService.approveUser(approval.id, {
          reviewNotes: reviewNotes || undefined,
          assignedRole: assignedRole || undefined,
          assignedPosition: assignedPosition || undefined,
          assignedHierarchies: Object.keys(selectedHierarchies).length > 0 ? selectedHierarchies : undefined,
          hierarchyLocation: Object.keys(selectedHierarchies).length > 0 ? {
            geo_id: selectedHierarchies['geographic'] || undefined,
            func_id: selectedHierarchies['functional'] || undefined,
            bus_id: selectedHierarchies['business'] || undefined
          } : undefined
        });
        toast.success(t('userApproval.approved') || 'User approved successfully');
      } else if (action === 'reject') {
        await userApprovalService.rejectUser(approval.id, reviewNotes || undefined);
        toast.success(t('userApproval.rejected') || 'User request rejected');
      } else if (action === 'merge') {
        if (!mergeToTenantId) {
          toast.error(t('userApproval.selectTenant') || 'Please select a tenant to merge to');
          return;
        }
        await userApprovalService.mergeToTenant(approval.id, mergeToTenantId, reviewNotes || undefined);
        toast.success(t('userApproval.merged') || 'User merged to existing tenant');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(`Error ${action}ing user:`, error);
      toast.error(error.message || `Failed to ${action} user`);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (action) {
      case 'approve':
        return t('userApproval.modal.approveTitle') || 'Approve User Request';
      case 'reject':
        return t('userApproval.modal.rejectTitle') || 'Reject User Request';
      case 'merge':
        return t('userApproval.modal.mergeTitle') || 'Merge to Existing Tenant';
      default:
        return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      size="lg"
    >
      <div className="space-y-6">
        {/* User Info Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>{t('userApproval.user') || 'User'}:</strong> {approval.first_name} {approval.last_name} ({approval.email})
          </p>
          {approval.requested_company_name && (
            <p className="text-sm text-gray-600 mt-1">
              <strong>{t('userApproval.companyName') || 'Company'}:</strong> {approval.requested_company_name}
            </p>
          )}
        </div>

        {/* Approve: Role and Hierarchy Assignment */}
        {action === 'approve' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userApproval.assignRole') || 'Assign Role'} *
              </label>
              <Select
                value={assignedRole}
                onChange={(e) => setAssignedRole(e.target.value)}
                options={[
                  { value: '', label: t('common.select') || 'Select role...' },
                  ...SYSTEM_ROLES
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userApproval.assignPosition') || 'Assign Position'}
              </label>
              <Select
                value={assignedPosition}
                onChange={(e) => setAssignedPosition(e.target.value)}
                options={[
                  { value: '', label: t('common.select') || 'Select position...' },
                  ...positions.map(p => ({ value: p.id, label: p.position_name }))
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userApproval.assignHierarchies') || 'Assign Hierarchies'}
              </label>
              <div className="space-y-2">
                {hierarchies.map((hierarchy) => (
                  <div key={hierarchy.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!selectedHierarchies[hierarchy.id]}
                      onChange={(e) => {
                        const updated = { ...selectedHierarchies };
                        if (e.target.checked) {
                          updated[hierarchy.id] = hierarchy.type;
                        } else {
                          delete updated[hierarchy.id];
                        }
                        setSelectedHierarchies(updated);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-700">
                      {hierarchy.name} ({hierarchy.type})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Merge: Tenant Selection */}
        {action === 'merge' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('userApproval.mergeToTenant') || 'Merge to Tenant'} *
            </label>
            <Select
              value={mergeToTenantId}
              onChange={(e) => setMergeToTenantId(e.target.value)}
              options={[
                { value: '', label: t('common.select') || 'Select tenant...' },
                ...availableTenants.map(t => ({ value: t.id, label: t.name }))
              ]}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('userApproval.mergeDescription') || 
                'This will merge the user into the selected existing tenant instead of creating a new one.'}
            </p>
          </div>
        )}

        {/* Review Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('userApproval.reviewNotes') || 'Review Notes'}
            {action === 'reject' && ' *'}
          </label>
          <Textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder={t('userApproval.reviewNotesPlaceholder') || 'Add any notes about this decision...'}
            rows={4}
            required={action === 'reject'}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (action === 'approve' && !assignedRole) || (action === 'merge' && !mergeToTenantId)}
            className={
              action === 'approve' ? 'bg-green-600 hover:bg-green-700' :
              action === 'reject' ? 'bg-red-600 hover:bg-red-700' :
              'bg-blue-600 hover:bg-blue-700'
            }
          >
            {loading
              ? (t('common.processing') || 'Processing...')
              : action === 'approve'
              ? (t('userApproval.approve') || 'Approve')
              : action === 'reject'
              ? (t('userApproval.reject') || 'Reject')
              : (t('userApproval.merge') || 'Merge')
            }
          </Button>
        </div>
      </div>
    </Modal>
  );
};

